import { hyperEVM } from '@/config/chain/constants'
import { ACCOUNT_LENS_ADDRESS, EVC_ADDRESS } from '@/config/euler-contracts'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useBorrowPairs } from '@/features/eborrow/logic/useBorrowPairs'
import { useVaultClassifier } from '@/features/card/contexts/VaultClassifierContext'
import { accountLensAbi } from 'abis/evm/accountLens'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useMemo } from 'react'
import { Address, formatUnits } from 'viem'
import { useAccount, useReadContracts } from 'wagmi'
import { getEulerCollateralState } from '../components/cardConfiguration.utils'
import { type AddCardWizardOption } from '../components/addCardWizard.utils'
import { HYPURRFI_TOKENS, type SolverType } from '../constants'
import { type MergedPosition, useAllEulerPositions } from './useAllEulerPositions'
import { useEulerCardData } from './useEulerCardData'
import { useHypurrFiCardData } from './useHypurrFiCardData'
import { useSolverOptions } from './useSolverOptions'

export interface AutoSelectionResult {
  bestOption: AddCardWizardOption | null
  bestSolverType: SolverType | null
  bestCardSolverConfig: Record<string, unknown> | null
  optionValues: Record<string, number>
  optionDetails: Record<string, SolverOptionDetail>
  scores: Record<string, number>
  isLoading: boolean
  isLoadingEulerPositions: boolean
  allEulerPositions: MergedPosition[]
  eulerSpendData: ReturnType<typeof useEulerCardData>
}

export interface SolverOptionDetail {
  positionCount?: number
  positionLabel?: string
  statusLabel?: string
  metricLabel?: string
  metricValue?: string
  secondaryValue?: string
  helperText?: string
}

interface EulerBorrowRouteCandidate {
  optionKey: 'euler-borrow-prime' | 'euler-borrow-yield'
  borrowVaultAddress: string
  subAccountId: number
  collateralUsd: number
}

/**
 * Hook that scores every available card solver option based on the user's
 * on-chain positions and returns the best one for auto-selection.
 *
 * Used both by the Add Card Wizard (to pre-select a default) and by
 * useUserCards (to create the very first card automatically with the
 * most valuable configuration).
 */
export function useCardAutoSelection(enabled: boolean): AutoSelectionResult {
  const { address } = useAccount()
  const { resolveMarket } = useVaultClassifier()
  const {
    hypurrFiBorrowTokens,
    hypurrFiWithdrawTokens,
    primeEulerVaults,
    primeEulerSpendVaults,
    yieldEulerVaults,
    yieldEulerSpendVaults,
    isLoading: isSolverOptionsLoading,
  } = useSolverOptions()

  const pooledData = useHypurrFiCardData({
    chainId: hyperEVM.id,
    enabled,
  })

  const { positions: allEulerPositions, isLoading: isEulerPositionsLoading } = useAllEulerPositions({
    enabled: enabled && !!address,
  })

  const eulerSpendData = useEulerCardData({
    chainId: hyperEVM.id,
    borrowVaultAddress: undefined,
    userAddress: address,
    enabled: enabled && !!address,
    subAccountId: undefined,
  })

  const { pairs: eulerBorrowPairs, isLoading: isEulerBorrowPairsLoading } = useBorrowPairs(address)

  const supportedEulerPositions = useMemo(
    () => allEulerPositions.filter((position) => !position.hasUnsupportedDebt),
    [allEulerPositions],
  )

  const routeCandidates = useMemo(
    () =>
      getEulerBorrowRouteCandidates({
        positions: supportedEulerPositions,
        borrowPairs: eulerBorrowPairs,
        primeEulerVaults,
        yieldEulerVaults,
      }),
    [supportedEulerPositions, eulerBorrowPairs, primeEulerVaults, yieldEulerVaults],
  )

  // Probe real spending power for every unique (sub-account, liability vault) route
  // so we can pick the best Euler sub-account by actual borrow headroom rather than a
  // static route score. Sub-accounts that return zero are treated as savings-only
  // (no enabled collateral for the target liability) and excluded from auto-selection.
  const spendingPowerProbeEntries = useMemo(() => {
    if (!address) return []
    const seen = new Map<string, { subAccountId: number; borrowVaultAddress: string; subAccountAddress: Address }>()
    for (const candidate of routeCandidates) {
      const key = `${candidate.subAccountId}-${candidate.borrowVaultAddress.toLowerCase()}`
      if (seen.has(key)) continue
      seen.set(key, {
        subAccountId: candidate.subAccountId,
        borrowVaultAddress: candidate.borrowVaultAddress,
        subAccountAddress: getSubAccountAddress(address, candidate.subAccountId),
      })
    }
    return Array.from(seen.values())
  }, [address, routeCandidates])

  const { data: spendingPowerProbeResults, isLoading: isLoadingSpendingPowerProbe } = useReadContracts({
    contracts: spendingPowerProbeEntries.map((entry) => ({
      address: ACCOUNT_LENS_ADDRESS,
      abi: accountLensAbi,
      functionName: 'getAccountInfo' as const,
      args: [entry.subAccountAddress, entry.borrowVaultAddress as Address] as const,
      chainId: hyperEVM.id,
    })),
    query: {
      enabled: enabled && !!address && spendingPowerProbeEntries.length > 0,
      staleTime: 30_000,
    },
  })

  const probeSpendingPowerByKey = useMemo(() => {
    const map = new Map<string, number>()
    if (!spendingPowerProbeResults) return map

    spendingPowerProbeResults.forEach((result, index) => {
      const entry = spendingPowerProbeEntries[index]
      if (!entry || result?.status !== 'success') return
      const liquidityInfo = (result.result as { vaultAccountInfo?: { liquidityInfo?: any } })?.vaultAccountInfo
        ?.liquidityInfo
      if (!liquidityInfo || liquidityInfo.queryFailure) return

      const maxBorrowCapacityUSD = Number(formatUnits(liquidityInfo.collateralValueBorrowing ?? 0n, 18))
      const liabilityBorrowUSD = Number(formatUnits(liquidityInfo.liabilityValueBorrowing ?? 0n, 18))
      const spendingPower = Math.max(maxBorrowCapacityUSD - liabilityBorrowUSD, 0)

      map.set(`${entry.subAccountId}-${entry.borrowVaultAddress.toLowerCase()}`, spendingPower)
    })

    return map
  }, [spendingPowerProbeEntries, spendingPowerProbeResults])

  // --- LTV-weighted fallback (mirrors AddCardWizard / CardConfiguration) ---
  // When the lens probe returns queryFailure (e.g. sub-account has no controller
  // attached yet, so the lens can't report borrow headroom), the wizard/config
  // fall back to Σ depositUSD × borrowLTV(borrowVault, collateralVault) over
  // each collateral that is (a) enabled on the sub-account via EVC and
  // (b) in the same market as the liability vault.

  // Batch 1: EVC.getCollaterals(subAccount) for every unique sub-account
  const uniqueSubAccountEntries = useMemo(() => {
    if (!address) return [] as { subAccountId: number; subAccountAddress: Address }[]
    const seen = new Map<number, { subAccountId: number; subAccountAddress: Address }>()
    for (const candidate of routeCandidates) {
      if (seen.has(candidate.subAccountId)) continue
      seen.set(candidate.subAccountId, {
        subAccountId: candidate.subAccountId,
        subAccountAddress: getSubAccountAddress(address, candidate.subAccountId),
      })
    }
    return Array.from(seen.values())
  }, [address, routeCandidates])

  const { data: enabledCollateralsResults, isLoading: isLoadingEnabledCollaterals } = useReadContracts({
    contracts: uniqueSubAccountEntries.map((entry) => ({
      address: EVC_ADDRESS,
      abi: eulerVaultConnectorAbi,
      functionName: 'getCollaterals' as const,
      args: [entry.subAccountAddress] as const,
      chainId: hyperEVM.id,
    })),
    query: {
      enabled: enabled && !!address && uniqueSubAccountEntries.length > 0,
      staleTime: 30_000,
    },
  })

  const enabledCollateralsBySubAccount = useMemo(() => {
    const map = new Map<number, Set<string>>()
    if (!enabledCollateralsResults) return map
    enabledCollateralsResults.forEach((result, index) => {
      const entry = uniqueSubAccountEntries[index]
      if (!entry || result?.status !== 'success') return
      const vaults = (result.result as readonly Address[]).map((a) => a.toLowerCase())
      map.set(entry.subAccountId, new Set(vaults))
    })
    return map
  }, [uniqueSubAccountEntries, enabledCollateralsResults])

  // Batch 2: LTVFull(collateralVault) on each unique (borrowVault, collateralVault) pair.
  // For every route candidate we consider all collaterals on its sub-account, since
  // any of them may contribute LTV-weighted spending power against the target liability.
  const ltvProbePairs = useMemo(() => {
    const seen = new Map<string, { borrowVault: Address; collateralVault: Address }>()
    for (const candidate of routeCandidates) {
      const position = supportedEulerPositions.find((p) => p.subAccountId === candidate.subAccountId)
      if (!position) continue
      for (const col of position.collateral) {
        const key = `${candidate.borrowVaultAddress.toLowerCase()}-${col.vaultAddress.toLowerCase()}`
        if (seen.has(key)) continue
        seen.set(key, {
          borrowVault: candidate.borrowVaultAddress as Address,
          collateralVault: col.vaultAddress as Address,
        })
      }
    }
    return Array.from(seen.values())
  }, [routeCandidates, supportedEulerPositions])

  const { data: ltvProbeResults, isLoading: isLoadingLtvProbe } = useReadContracts({
    contracts: ltvProbePairs.map((p) => ({
      address: p.borrowVault,
      abi: eVaultImplementationAbi,
      functionName: 'LTVFull' as const,
      args: [p.collateralVault] as const,
      chainId: hyperEVM.id,
    })),
    query: {
      enabled: enabled && ltvProbePairs.length > 0,
      staleTime: 30_000,
    },
  })

  const borrowLtvByPair = useMemo(() => {
    const map = new Map<string, number>()
    if (!ltvProbeResults) return map
    ltvProbeResults.forEach((result, index) => {
      const pair = ltvProbePairs[index]
      if (!pair || result?.status !== 'success') return
      const [borrowLTV] = result.result as readonly [number, number, number, number, number]
      const key = `${pair.borrowVault.toLowerCase()}-${pair.collateralVault.toLowerCase()}`
      map.set(key, (borrowLTV ?? 0) / 10000)
    })
    return map
  }, [ltvProbePairs, ltvProbeResults])

  // Combine: prefer lens value; otherwise use LTV-weighted fallback.
  const effectiveSpendingPowerByKey = useMemo(() => {
    const map = new Map<string, number>(probeSpendingPowerByKey)

    for (const entry of spendingPowerProbeEntries) {
      const routeKey = `${entry.subAccountId}-${entry.borrowVaultAddress.toLowerCase()}`
      // Lens returned a usable value; keep it.
      if (probeSpendingPowerByKey.has(routeKey)) continue

      const position = supportedEulerPositions.find((p) => p.subAccountId === entry.subAccountId)
      if (!position) continue

      const enabledSet = enabledCollateralsBySubAccount.get(entry.subAccountId)
      // If we haven't loaded the enabled set yet, skip — we'll recompute on re-render.
      if (!enabledSet) continue

      const controllerMarketKey = resolveMarket(entry.borrowVaultAddress as Address)?.key
      let totalWeightedUSD = 0
      for (const col of position.collateral) {
        const colAddrLower = col.vaultAddress.toLowerCase()
        if (!enabledSet.has(colAddrLower)) continue
        if (controllerMarketKey) {
          const colMarketKey = resolveMarket(col.vaultAddress as Address)?.key
          if (colMarketKey !== controllerMarketKey) continue
        }
        const ltvKey = `${entry.borrowVaultAddress.toLowerCase()}-${colAddrLower}`
        const ltv = borrowLtvByPair.get(ltvKey)
        if (ltv == null) continue
        totalWeightedUSD += col.depositAmountUSD * ltv
      }

      // Subtract any existing debt on this sub-account so we don't overcount.
      const debtUSD = position.debtVaults.reduce((sum, debt) => sum + (debt.borrowAmount ?? 0), 0)
      const spendingPower = Math.max(totalWeightedUSD - debtUSD, 0)

      if (spendingPower > 0) {
        map.set(routeKey, spendingPower)
      }
    }

    return map
  }, [
    probeSpendingPowerByKey,
    spendingPowerProbeEntries,
    supportedEulerPositions,
    enabledCollateralsBySubAccount,
    borrowLtvByPair,
    resolveMarket,
  ])

  const bestPrimeRoute = useMemo(
    () => pickBestEulerBorrowRouteBySpendingPower(routeCandidates, 'euler-borrow-prime', effectiveSpendingPowerByKey),
    [routeCandidates, effectiveSpendingPowerByKey],
  )
  const bestYieldRoute = useMemo(
    () => pickBestEulerBorrowRouteBySpendingPower(routeCandidates, 'euler-borrow-yield', effectiveSpendingPowerByKey),
    [routeCandidates, effectiveSpendingPowerByKey],
  )

  const primePreviewData = useEulerCardData({
    chainId: hyperEVM.id,
    borrowVaultAddress: bestPrimeRoute?.address as Address | undefined,
    userAddress: address,
    enabled: enabled && !!address && !!bestPrimeRoute,
    subAccountId: bestPrimeRoute?.subAccountId,
  })

  const yieldPreviewData = useEulerCardData({
    chainId: hyperEVM.id,
    borrowVaultAddress: bestYieldRoute?.address as Address | undefined,
    userAddress: address,
    enabled: enabled && !!address && !!bestYieldRoute,
    subAccountId: bestYieldRoute?.subAccountId,
  })

  // Settled-state guard: once we know a user has Euler positions+pairs, we must
  // wait for the LTV / enabled-collaterals probes to actually return before
  // declaring auto-selection "done". Otherwise, the first render after
  // `enabled` flips briefly reports `isLoading: false` (queries haven't been
  // registered with wagmi yet) and the auto-create gate fires prematurely
  // with `bestOption: null`.
  const hasEulerRouteData = routeCandidates.length > 0
  const eulerProbesPending =
    hasEulerRouteData &&
    (!enabledCollateralsResults ||
      (ltvProbePairs.length > 0 && !ltvProbeResults) ||
      (spendingPowerProbeEntries.length > 0 && !spendingPowerProbeResults))

  const isLoading =
    isSolverOptionsLoading ||
    (enabled &&
      (isEulerPositionsLoading ||
        eulerSpendData.isLoadingPositionData ||
        isEulerBorrowPairsLoading ||
        (spendingPowerProbeEntries.length > 0 && isLoadingSpendingPowerProbe) ||
        (uniqueSubAccountEntries.length > 0 && isLoadingEnabledCollaterals) ||
        (ltvProbePairs.length > 0 && isLoadingLtvProbe) ||
        primePreviewData.isLoadingPositionData ||
        yieldPreviewData.isLoadingPositionData ||
        eulerProbesPending))

  const availableOptions = useMemo<AddCardWizardOption[]>(() => {
    const options: AddCardWizardOption[] = []

    // Auto-selection only considers borrow modes — spend/withdraw must be
    // chosen explicitly by the user in the wizard.
    if (hypurrFiBorrowTokens.length > 0) {
      options.push({
        key: 'hypurrFi-borrow',
        title: 'Pooled',
        subtitle: 'HypurrFi pooled lending',
        market: 'hypurrFi',
        mode: 'borrow',
        solverType: 'hypurrFi',
      })
    }

    if (primeEulerVaults.length > 0) {
      options.push({
        key: 'euler-borrow-prime',
        title: 'Prime',
        subtitle: 'HypurrFi Prime market',
        market: 'euler',
        mode: 'borrow',
        solverType: 'euler',
        cluster: 'prime',
      })
    }

    if (yieldEulerVaults.length > 0) {
      options.push({
        key: 'euler-borrow-yield',
        title: 'Yield',
        subtitle: 'HypurrFi Yield market',
        market: 'euler',
        mode: 'borrow',
        solverType: 'euler',
        cluster: 'yield',
      })
    }

    return options
  }, [hypurrFiBorrowTokens.length, primeEulerVaults.length, yieldEulerVaults.length])

  const optionValues = useMemo<Record<string, number>>(() => {
    const values: Record<string, number> = {}

    values['hypurrFi-borrow'] = pooledData.maxBorrowCapacityUSD || 0

    // Prefer the lens-or-LTV-fallback value already computed in
    // `effectiveSpendingPowerByKey`. Falls back to the preview-data derived
    // value (lens only) to preserve prior behaviour when no fallback was
    // needed.
    const primeValue = bestPrimeRoute
      ? effectiveSpendingPowerByKey.get(`${bestPrimeRoute.subAccountId}-${bestPrimeRoute.address.toLowerCase()}`) ??
        getEulerBorrowPreviewValue(bestPrimeRoute, primePreviewData) ??
        0
      : null
    if (primeValue != null) {
      values['euler-borrow-prime'] = primeValue
    }

    const yieldValue = bestYieldRoute
      ? effectiveSpendingPowerByKey.get(`${bestYieldRoute.subAccountId}-${bestYieldRoute.address.toLowerCase()}`) ??
        getEulerBorrowPreviewValue(bestYieldRoute, yieldPreviewData) ??
        0
      : null
    if (yieldValue != null) {
      values['euler-borrow-yield'] = yieldValue
    }

    if (hypurrFiWithdrawTokens.length > 0) {
      values['hypurrFi-spend'] = pooledData.spendModeCollateralPositions.reduce((max, position) => {
        const spendingPower = 'spendingPowerUSD' in position ? position.spendingPowerUSD.toNumber() : 0
        return Math.max(max, spendingPower)
      }, 0)
    }

    const primeSpendVaults = new Set(primeEulerSpendVaults.map((vault) => vault.address.toLowerCase()))
    const yieldSpendVaults = new Set(yieldEulerSpendVaults.map((vault) => vault.address.toLowerCase()))

    values['euler-spend-prime'] = eulerSpendData.spendModeCollateralPositions.reduce((max, position) => {
      if (!primeSpendVaults.has(position.vaultAddress.toLowerCase())) return max
      return Math.max(max, position.spendingPowerUSD.toNumber())
    }, 0)

    values['euler-spend-yield'] = eulerSpendData.spendModeCollateralPositions.reduce((max, position) => {
      if (!yieldSpendVaults.has(position.vaultAddress.toLowerCase())) return max
      return Math.max(max, position.spendingPowerUSD.toNumber())
    }, 0)

    return values
  }, [
    hypurrFiWithdrawTokens.length,
    pooledData.spendModeCollateralPositions,
    pooledData.maxBorrowCapacityUSD,
    primeEulerSpendVaults,
    yieldEulerSpendVaults,
    eulerSpendData.spendModeCollateralPositions,
    bestPrimeRoute,
    bestYieldRoute,
    primePreviewData,
    yieldPreviewData,
    effectiveSpendingPowerByKey,
  ])

  const scores = useMemo<Record<string, number>>(
    () => ({
      'hypurrFi-borrow': optionValues['hypurrFi-borrow'] ?? 0,
      'euler-borrow-prime': optionValues['euler-borrow-prime'] ?? 0,
      'euler-borrow-yield': optionValues['euler-borrow-yield'] ?? 0,
    }),
    [optionValues],
  )

  const optionDetails = useMemo<Record<string, SolverOptionDetail>>(() => {
    const details: Record<string, SolverOptionDetail> = {}

    const primeState = getEulerBorrowPreviewState({
      optionKey: 'euler-borrow-prime',
      routeCandidates,
      bestRoute: bestPrimeRoute,
      previewData: primePreviewData,
      positions: supportedEulerPositions,
      optionValues,
      resolveMarket,
    })
    if (primeState) details['euler-borrow-prime'] = primeState

    const yieldState = getEulerBorrowPreviewState({
      optionKey: 'euler-borrow-yield',
      routeCandidates,
      bestRoute: bestYieldRoute,
      previewData: yieldPreviewData,
      positions: supportedEulerPositions,
      optionValues,
      resolveMarket,
    })
    if (yieldState) details['euler-borrow-yield'] = yieldState

    return details
  }, [
    supportedEulerPositions,
    routeCandidates,
    bestPrimeRoute,
    bestYieldRoute,
    primePreviewData,
    yieldPreviewData,
    optionValues,
    resolveMarket,
  ])

  const bestOption = useMemo<AddCardWizardOption | null>(() => {
    if (availableOptions.length === 0) return null

    let bestKey: string | null = null
    let bestScore = 0

    for (const option of availableOptions) {
      const score = scores[option.key] || 0
      if (score > bestScore) {
        bestScore = score
        bestKey = option.key
      }
    }

    if (!bestKey || bestScore <= 0) return null

    return availableOptions.find((o) => o.key === bestKey) ?? null
  }, [availableOptions, scores])

  const bestCardSolverConfig = useMemo<Record<string, unknown> | null>(() => {
    if (!bestOption) return null

    if (bestOption.key === 'hypurrFi-borrow') {
      const bestToken = pickBestPooledBorrowToken(hypurrFiBorrowTokens.map((token) => token.address))
      if (bestToken) {
        return { borrowTokenAddress: bestToken }
      }
    }

    if (bestOption.mode === 'borrow' && bestOption.market === 'euler') {
      const result = bestOption.cluster === 'prime' ? bestPrimeRoute : bestYieldRoute
      if (result) {
        return { borrowVaultAddress: result.address, subAccountId: result.subAccountId }
      }
    }

    return null
  }, [bestOption, bestPrimeRoute, bestYieldRoute, hypurrFiBorrowTokens])

  return {
    bestOption,
    bestSolverType: bestOption?.solverType ?? null,
    bestCardSolverConfig,
    optionValues,
    optionDetails,
    scores,
    isLoading,
    isLoadingEulerPositions: enabled && isEulerPositionsLoading,
    allEulerPositions,
    eulerSpendData,
  }
}

// --- Helpers ---

/**
 * Auto-selection always picks USDT as the pooled borrow token when it's in the
 * backend's supported list, falling back to the first supported token only when
 * USDT isn't available. The user can still override the token in the wizard.
 */
function pickBestPooledBorrowToken(supportedBorrowTokenAddresses: string[]): string | null {
  if (supportedBorrowTokenAddresses.length === 0) return null

  const usdtLower = HYPURRFI_TOKENS.USDT.toLowerCase()
  const usdtMatch = supportedBorrowTokenAddresses.find((address) => address.toLowerCase() === usdtLower)

  return usdtMatch ?? supportedBorrowTokenAddresses[0] ?? null
}

interface VaultWithSubAccount {
  address: string
  subAccountId: number
}

function getEulerBorrowRouteCandidates(params: {
  positions: MergedPosition[]
  borrowPairs: Array<{
    collateral?: string
    liability?: string
  }>
  primeEulerVaults: { address: string }[]
  yieldEulerVaults: { address: string }[]
}): EulerBorrowRouteCandidate[] {
  const primeBorrowVaultAddrs = new Set(params.primeEulerVaults.map((v) => v.address.toLowerCase()))
  const yieldBorrowVaultAddrs = new Set(params.yieldEulerVaults.map((v) => v.address.toLowerCase()))
  const candidates: EulerBorrowRouteCandidate[] = []

  for (const position of params.positions) {
    const existingDebtVaults = new Set(position.debtVaults.map((debt) => debt.liabilityVaultAddress.toLowerCase()))

    for (const collateral of position.collateral) {
      const matchingPairs = params.borrowPairs.filter((pair) => {
        if (!pair.collateral || !pair.liability) return false
        if (pair.collateral.toLowerCase() !== collateral.vaultAddress.toLowerCase()) return false

        // When the position already has debt, only consider the active liability vault.
        if (existingDebtVaults.size > 0 && !existingDebtVaults.has(pair.liability.toLowerCase())) {
          return false
        }

        return true
      })

      for (const pair of matchingPairs) {
        const liabilityLower = pair.liability!.toLowerCase()
        const optionKey = primeBorrowVaultAddrs.has(liabilityLower)
          ? 'euler-borrow-prime'
          : yieldBorrowVaultAddrs.has(liabilityLower)
            ? 'euler-borrow-yield'
            : null

        if (!optionKey) continue

        candidates.push({
          optionKey,
          borrowVaultAddress: pair.liability!,
          subAccountId: position.subAccountId,
          collateralUsd: collateral.depositAmountUSD,
        })
      }
    }
  }

  return candidates
}

/**
 * Pick the candidate for a given cluster with the highest actual spending power.
 *
 * Candidates whose probed spending power is 0 are excluded — typically savings-only
 * sub-accounts (deposits not enabled as collateral for the target liability), but
 * also fully-utilized positions (no remaining borrow headroom). Either way, a card
 * routed through them couldn't borrow new funds, so they're never auto-selected.
 * The wizard exposes those sub-accounts separately if the user wants to enable them.
 */
function pickBestEulerBorrowRouteBySpendingPower(
  candidates: EulerBorrowRouteCandidate[],
  optionKey: EulerBorrowRouteCandidate['optionKey'],
  spendingPowerByKey: Map<string, number>,
): VaultWithSubAccount | null {
  let best: { candidate: EulerBorrowRouteCandidate; spendingPower: number } | null = null

  for (const candidate of candidates) {
    if (candidate.optionKey !== optionKey) continue

    const key = `${candidate.subAccountId}-${candidate.borrowVaultAddress.toLowerCase()}`
    const spendingPower = spendingPowerByKey.get(key) ?? 0
    if (spendingPower <= 0) continue

    if (
      !best ||
      spendingPower > best.spendingPower ||
      (spendingPower === best.spendingPower && candidate.collateralUsd > best.candidate.collateralUsd)
    ) {
      best = { candidate, spendingPower }
    }
  }

  if (!best) return null

  return {
    address: best.candidate.borrowVaultAddress,
    subAccountId: best.candidate.subAccountId,
  }
}

function formatOptionUsdValue(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

function getEulerBorrowPreviewValue(
  bestRoute: VaultWithSubAccount | null,
  previewData: { maxBorrowCapacityUSD?: number; totalDebtBorrowUSD?: number; enabledCollateralVaults?: string[] },
): number | null {
  if (!bestRoute) return null

  return Math.max((previewData.maxBorrowCapacityUSD ?? 0) - (previewData.totalDebtBorrowUSD ?? 0), 0)
}

function getEulerBorrowPreviewState(params: {
  optionKey: EulerBorrowRouteCandidate['optionKey']
  routeCandidates: EulerBorrowRouteCandidate[]
  bestRoute: VaultWithSubAccount | null
  previewData: {
    enabledCollateralVaults?: string[]
    maxBorrowCapacityUSD?: number
    totalDebtBorrowUSD?: number
  }
  positions: MergedPosition[]
  optionValues: Record<string, number>
  resolveMarket: (vaultAddress: Address) => { key?: string } | undefined
}): SolverOptionDetail | null {
  const relatedCandidates = params.routeCandidates.filter((candidate) => candidate.optionKey === params.optionKey)
  if (relatedCandidates.length === 0 || !params.bestRoute) return null

  const bestPosition = params.positions.find((position) => position.subAccountId === params.bestRoute?.subAccountId)
  if (!bestPosition) return null

  const controllerMarketKey = params.resolveMarket(params.bestRoute.address as Address)?.key
  const collateralState = getEulerCollateralState({
    collateral: bestPosition.collateral,
    enabledVaults: (params.previewData.enabledCollateralVaults ?? []).map((a) => a.toLowerCase()),
    controllerMarketKey,
    resolveMarket: params.resolveMarket,
  })

  return {
    positionCount: [...new Set(relatedCandidates.map((candidate) => candidate.subAccountId))].length,
    positionLabel: `Position ${bestPosition.subAccountId}`,
    statusLabel: collateralState.savingsOnly ? 'Savings only' : undefined,
    metricLabel: 'Spending Power',
    metricValue: formatOptionUsdValue(collateralState.savingsOnly ? 0 : params.optionValues[params.optionKey] ?? 0),
    secondaryValue: collateralState.savingsOnly
      ? `${formatOptionUsdValue(collateralState.savingsUSD)} not enabled`
      : collateralState.hasEnabledCollateral && collateralState.hasSavings
        ? `+ ${formatOptionUsdValue(collateralState.savingsUSD)} in savings`
        : undefined,
    helperText: collateralState.savingsOnly
      ? 'Select this position to enable your deposits as collateral and unlock spending power.'
      : undefined,
  }
}
